import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  hasPendingPostLoginRedirect,
  usePostLoginRedirect,
} from './usePostLoginRedirect'
import * as Sentry from '@sentry/nextjs'
import { useRouter } from 'next/router'

import Keycloak from 'keycloak-js'

import { useMeQuery } from '@/graphql/generated/hooks'
import { useAppDispatch } from '@/redux/hooks'
import { setMe } from '@/redux/me/meSlice'
import {
  resetNotifications,
  setNotifications,
} from '@/redux/notifications/notificationsSlice'
import { resetUi } from '@/redux/ui/uiSlice'
import { mixpanel } from '@/utils/analytics'

import { tokenManager } from './tokenManager'
import { AuthClientEvent, AuthClientTokens, ROLE_STATUS } from './types'
import {
  MIN_TOKEN_VALIDITY_IN_MINUTES,
  getInitOptions,
  getKeycloakInstance,
  getRoleStatus,
  isValidToken,
} from './utils'

interface KeyCloakProviderIProps {
  autoRefreshToken?: boolean
  children: ReactNode
}

type KeyCloakProviderContextT = {
  keycloak: Keycloak
  logIn: () => void
  logOut: () => void
  isKcInitilised: boolean
  isValidLogin: boolean
  isResolvingRedirect: boolean
  roleStatus: ROLE_STATUS
}

export const KeyCloakContext = createContext<KeyCloakProviderContextT>(null)

export const KeyCloakProvider = ({
  autoRefreshToken,
  children,
}: KeyCloakProviderIProps) => {
  const dispatch = useAppDispatch()
  const router = useRouter()
  const [keycloak] = useState<Keycloak>(getKeycloakInstance())
  const { clear, restoreIfNeeded, saveCurrentLocation } = usePostLoginRedirect()
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [isKcInitilised, setIsKcInitilised] = useState(false)
  // Gate the children render only when there's a pending post-login redirect to
  // avoid the brief flash of "/" before router.replace navigates to the saved
  // path. In normal cold loads this stays false from the first render.
  const [isResolvingRedirect, setIsResolvingRedirect] = useState(() => {
    return hasPendingPostLoginRedirect()
  })
  const roleStatus = getRoleStatus(accessToken)
  const isValidLogin = useMemo(
    () =>
      roleStatus !== ROLE_STATUS.NOT_SET &&
      isValidToken(accessToken) &&
      isKcInitilised,
    [accessToken, isKcInitilised, roleStatus]
  )
  const {
    data,
    loading: isLoading,
    client,
  } = useMeQuery({
    fetchPolicy: 'network-only',
    skip: !isValidLogin,
  })
  const user = data?.me

  const initializeKeyCloak = () => {
    // Attach Keycloak listeners
    keycloak.onReady = updateState('onReady')
    keycloak.onAuthSuccess = updateState('onAuthSuccess')
    keycloak.onAuthError = updateState('onAuthError')
    keycloak.onAuthRefreshSuccess = updateState('onAuthRefreshSuccess')
    keycloak.onAuthRefreshError = updateState('onAuthRefreshError')
    keycloak.onAuthLogout = updateState('onAuthLogout')
    keycloak.onTokenExpired = refreshToken('onTokenExpired')

    keycloak.init({ ...getInitOptions() }).catch(updateState('onInitError'))
  }

  const updateState = (event: AuthClientEvent) => () => {
    onEvent(event)
    const { idToken, refreshToken, token } = keycloak
    onTokens({
      idToken,
      refreshToken,
      token,
    })
  }

  const refreshToken = (event: AuthClientEvent) => () => {
    onEvent(event)
    if (autoRefreshToken !== false) {
      // Refresh Keycloak token
      keycloak.updateToken(MIN_TOKEN_VALIDITY_IN_MINUTES)
    }
  }

  const onEvent = async (event: AuthClientEvent) => {
    switch (event) {
      case 'onAuthError': {
        keycloak.clearToken()
        break
      }
      case 'onAuthLogout': {
        resetReduxState()
        client.resetStore()
        mixpanel.track('Logout')
        Sentry.setUser(null)
        break
      }
      case 'onAuthRefreshError': {
        handleInvalidSession()
        break
      }
      case 'onAuthRefreshSuccess': {
        break
      }
      case 'onAuthSuccess': {
        mixpanel.track('Login', {
          type: 'KC',
          realm: keycloak.realm,
        })
        break
      }
      case 'onInitError': {
        break
      }
      case 'onReady': {
        handleOnReady()
        break
      }
      case 'onTokenExpired': {
        break
      }
      default:
        break
    }
  }

  const onTokens = useCallback((tokens: AuthClientTokens) => {
    if (!tokens.token) {
      return
    }

    tokenManager.setTokens(tokens)
    setAccessToken(tokens.token)
  }, [])

  const logIn = async () => {
    try {
      saveCurrentLocation()
      window.localStorage.removeItem('viewport')
      await keycloak.login({ redirectUri: window.location.origin })
    } catch (error) {
      // TODO Handle error
    }
  }

  const logOut = async () => {
    try {
      clear()
      await keycloak.logout({ redirectUri: window.location.origin })
    } catch (error) {
      // TODO Handle error
    }
  }

  const handleOnReady = async () => {
    try {
      // Check if a validation token, if not update
      if (keycloak.isTokenExpired()) {
        await keycloak.updateToken(-1)
      }

      setIsKcInitilised(true)
      const hasRedirected = restoreIfNeeded()
      if (!hasRedirected) setIsResolvingRedirect(false)
    } catch {
      // No token, redirect to login
      setIsResolvingRedirect(false)
      logIn()
    }
  }

  const handleInvalidSession = async () => {
    keycloak.idToken = tokenManager.getIdToken()
    keycloak.logout()
  }

  const resetReduxState = () => {
    dispatch(resetUi())
    dispatch(setMe(false))
    dispatch(setNotifications([]))
    dispatch(resetNotifications())
    tokenManager.clearTokens()
    setAccessToken(null)
  }

  useEffect(() => {
    // Initialize Keycloak once on mount to register auth listeners and start
    // the login/session lifecycle for the app.
    initializeKeyCloak()
  }, [])

  useEffect(() => {
    if (!isResolvingRedirect) return
    const off = () => setIsResolvingRedirect(false)
    router.events.on('routeChangeComplete', off)
    router.events.on('routeChangeError', off)
    return () => {
      router.events.off('routeChangeComplete', off)
      router.events.off('routeChangeError', off)
    }
  }, [router, isResolvingRedirect])

  useEffect(() => {
    if (user && isValidLogin) {
      mixpanel.track('Login', {
        realm: keycloak.realm,
        type: 'GQL',
        firstName: user?.firstName,
        lastName: user?.lastName,
        role: user?.roleName,
        userName: user?.email,
        email: user?.email,
        userId: user?.id,
      })
      // Set Sentry user context so errors are associated with the logged-in user
      Sentry.setUser({
        id: user?.id,
        email: user?.email,
        username: user?.email,
        role: user?.roleName,
      })
      dispatch(setMe(user))
    }
  }, [isValidLogin, isLoading])

  return (
    <KeyCloakContext.Provider
      value={{
        keycloak,
        isKcInitilised,
        isValidLogin,
        isResolvingRedirect,
        roleStatus,
        logIn,
        logOut,
      }}
    >
      {children}
    </KeyCloakContext.Provider>
  )
}

export const useKeyCloak = () => {
  return useContext(KeyCloakContext)
}
